import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import type { Props as IClusterCellRendererProps } from '~/components/CellRenderers/ClusterCellRenderer'
import type { Props as IDetailedScoreCellRendererProps } from '~/components/CellRenderers/ClusterScoreCellRenderer'
import type { ICluster } from '~/types/cluster'

import ClusterCellRenderer from '~/components/CellRenderers/ClusterCellRenderer'
import ClusterVariationCellRenderer from '~/components/CellRenderers/ClusterVariationCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import { CLUSTER_STATUSES } from '~/const/clusters'
import { formatDecimalNumber, formatValue } from '~/utils/number'
import { detailedScoreColDef } from '~/utils/table'

export const COLUMNS = {
  CLUSTER: 'name',
  DOWNLOADS_D180: 'downloads_d180',
  DOWNLOADS_D180_US: 'downloads_d180_us',
  DOWNLOADS_D30: 'downloads_d30',
  DOWNLOADS_D30_US: 'downloads_d30_us',
  DOWNLOADS_D360: 'downloads_d360',
  DOWNLOADS_D360_US: 'downloads_d360_us',
  DOWNLOADS_D90: 'downloads_d90',
  DOWNLOADS_D90_US: 'downloads_d90_us',
  GROWTH_DOWNLOADS: 'growth_downloads',
  GROWTH_DOWNLOADS_US: 'growth_downloads_us',
  GROWTH_REVENUE: 'growth_revenue',
  GROWTH_REVENUE_US: 'growth_revenue_us',
  REVENUE_D180: 'revenue_d180',
  REVENUE_D180_US: 'revenue_d180_us',
  REVENUE_D30: 'revenue_d30',
  REVENUE_D30_US: 'revenue_d30_us',
  REVENUE_D360: 'revenue_d360',
  REVENUE_D360_US: 'revenue_d360_us',
  REVENUE_D90: 'revenue_d90',
  REVENUE_D90_US: 'revenue_d90_us',
  RPD: 'rpd',
  RPD_US: 'rpd_us',
  SCORE: 'score_cluster_total',
  SCORE_DOWNLOADS_D360: 'score_downloads_d360',
  SCORE_DOWNLOADS_D360_US: 'score_downloads_d360_us',
  SCORE_GROWTH_DOWNLOADS: 'score_growth_downloads',
  SCORE_GROWTH_DOWNLOADS_US: 'score_growth_downloads_us',
  SCORE_GROWTH_REVENUE: 'score_growth_revenue',
  SCORE_GROWTH_REVENUE_US: 'score_growth_revenue_us',
  SCORE_REVENUE_D360: 'score_revenue_d360',
  SCORE_REVENUE_D360_US: 'score_revenue_d360_us',
  SCORE_SOV_DOWNLOADS_D360_US: 'score_sov_downloads_d360_us',
  SCORE_SOV_REVENUE_D360_US: 'score_sov_revenue_d360_us',
  SOV_DOWNLOADS_US: 'sov_downloads_us',
  SOV_REVENUE_US: 'sov_revenue_us',
}

const COLUMN_WIDTH = {
  EXTRA_LARGE: 300,
  LARGE: 240,
  SMALL: 130,
}

const commonDefinition: ColDef = {
  menuTabs: [],
  suppressSizeToFit: true,
  width: COLUMN_WIDTH.SMALL,
}

const columns: ColDef[] = [
  {
    autoHeight: true,
    cellRenderer: ClusterCellRenderer,
    comparator: (a: IClusterCellRendererProps, b: IClusterCellRendererProps): number => {
      const statusLabelA = CLUSTER_STATUSES.find(({ value }) => value === a.status)?.label
      const statusLabelB = CLUSTER_STATUSES.find(({ value }) => value === b.status)?.label

      if (!statusLabelA) return 1
      if (!statusLabelB) return -1

      return statusLabelA.toLowerCase().localeCompare(statusLabelB.toLowerCase())
    },
    field: COLUMNS.CLUSTER,
    filterValueGetter: ({ data }: ValueGetterParams<ICluster>): string => `${data?.name} ${data?.cluster_id}` || '',
    headerName: 'Cluster',
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<ICluster>): IClusterCellRendererProps | undefined => {
      if (!data) return undefined

      return {
        description: data.description,
        iconUrl: data.icon_url,
        name: data.name,
        nbApps: data.nb_apps,
        status: data.status,
        tags: data.tags,
        userId: data.user_id,
      }
    },
    width: COLUMN_WIDTH.EXTRA_LARGE,
  },
  detailedScoreColDef({
    ...commonDefinition,
    field: COLUMNS.SCORE,
    headerName: 'Score',
    valueGetter: ({ data }: ValueGetterParams<ICluster>): IDetailedScoreCellRendererProps | undefined => {
      if (!data) return undefined

      return {
        cellScore: { label: 'Total', value: data.score_cluster_total },
        scale: 100,
        tooltipScores: [
          { label: 'Revenue D360', value: data.score_revenue_d360 },
          { label: 'Revenue D360 US', value: data.score_revenue_d360_us },
          { label: 'Downloads D360', value: data.score_downloads_d360 },
          { label: 'Downloads D360 US', value: data.score_downloads_d360_us },
          { label: 'SOV Downloads D360 US', value: data.score_sov_downloads_d360_us },
          { label: 'SOV Revenue D360 US', value: data.score_sov_revenue_d360_us },
          { label: 'Growth Revenue', value: data.score_growth_revenue },
          { label: 'Growth Revenue US', value: data.score_growth_revenue_us },
          { label: 'Growth Downloads', value: data.score_growth_downloads },
          { label: 'Growth Downloads US', value: data.score_growth_downloads_us },
        ],
      }
    },
  }),
  {
    ...commonDefinition,
    field: COLUMNS.RPD,
    headerName: 'RPD',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'rpd'>>): string =>
      value ? `${formatDecimalNumber(value, 3)}$` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.RPD_US,
    headerName: 'RPD 🇺🇸',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'rpd_us'>>): string =>
      value ? `${formatDecimalNumber(value, 3)}$` : '',
  },
  {
    ...commonDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.GROWTH_DOWNLOADS,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Downloads', headerTitle: 'Growth' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<ICluster>) =>
      data && data.growth_downloads ? data.growth_downloads * 100 : undefined,
  },
  {
    ...commonDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.GROWTH_REVENUE,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Revenue', headerTitle: 'Growth' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<ICluster>) =>
      data && data.growth_revenue ? data.growth_revenue * 100 : undefined,
  },
  {
    ...commonDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.GROWTH_DOWNLOADS_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Downloads 🇺🇸', headerTitle: 'Growth' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<ICluster>) =>
      data && data.growth_downloads_us ? data.growth_downloads_us * 100 : undefined,
  },
  {
    ...commonDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.GROWTH_REVENUE_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Revenue 🇺🇸', headerTitle: 'Growth' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<ICluster>) =>
      data && data.growth_revenue_us ? data.growth_revenue_us * 100 : undefined,
  },
  {
    ...commonDefinition,
    field: COLUMNS.DOWNLOADS_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'downloads_d30'>>): string =>
      value ? formatValue(value) : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.REVENUE_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'revenue_d30'>>): string =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.DOWNLOADS_D360,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'downloads_d360'>>): string =>
      value ? formatValue(value) : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.REVENUE_D360,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'revenue_d360'>>): string =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.DOWNLOADS_D30_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30 🇺🇸', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'downloads_d30_us'>>): string =>
      value ? formatValue(value) : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.REVENUE_D30_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30 🇺🇸', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'revenue_d30_us'>>): string =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.DOWNLOADS_D360_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360 🇺🇸', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'downloads_d360_us'>>): string =>
      value ? formatValue(value) : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.REVENUE_D360_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360 🇺🇸', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'revenue_d360_us'>>): string =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.DOWNLOADS_D180,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D180', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'downloads_d180'>>): string =>
      value ? formatValue(value) : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.REVENUE_D180,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D180', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'revenue_d180'>>): string =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.DOWNLOADS_D90,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D90', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'downloads_d90'>>): string =>
      value ? formatValue(value) : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.REVENUE_D90,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D90', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'revenue_d90'>>): string =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SOV_DOWNLOADS_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Downloads 🇺🇸', headerTitle: 'SOV' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'sov_downloads_us'>>): string =>
      value ? `${formatDecimalNumber(value * 100)}%` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SOV_REVENUE_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'Revenue 🇺🇸', headerTitle: 'SOV' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<ICluster, 'sov_revenue_us'>>): string =>
      value ? `${formatDecimalNumber(value * 100)}%` : '',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_REVENUE_D360,
    headerName: 'Score Revenue D360',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_REVENUE_D360_US,
    headerName: 'Score Revenue D360 US',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_DOWNLOADS_D360,
    headerName: 'Score Downloads D360',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_DOWNLOADS_D360_US,
    headerName: 'Score Downloads D360 US',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_SOV_DOWNLOADS_D360_US,
    headerName: 'Score SOV Downloads D360 US',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_SOV_REVENUE_D360_US,
    headerName: 'Score SOV Revenue D360 US',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_GROWTH_REVENUE,
    headerName: 'Score Growth Revenue',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_GROWTH_REVENUE_US,
    headerName: 'Score Growth Revenue US',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_GROWTH_DOWNLOADS,
    headerName: 'Score Growth Downloads',
  },
  {
    ...commonDefinition,
    field: COLUMNS.SCORE_GROWTH_DOWNLOADS_US,
    headerName: 'Score Growth Downloads US',
  },
]

export default columns
